import React, { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { Link } from "react-router-dom";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { Modal } from "react-bootstrap";
import CollapseHeader from "../../../core/common/collapse-header";
import Datatable from "../../../core/common/dataTable";
import { Client, fetchClients } from "../../../api/client";
import { Plan, fetchPlans } from "../../../api/useplan";
import {
  createPayment,
  Payment,
  deletePayment,
  updatePayment,
  getPaymentById,
  getAllPayments,
  UpdatePaymentRequest,
  CreatePaymentRequest,
} from "../../../api/clientpayment";
import { toast } from "react-toastify";
import { highlightMatch } from "../../../core/common/highlightmatch";
import { initialSettings } from "../../../core/common/selectoption/selectoption";

type PaymentColumn =
  | "payment_id"
  | "client_id"
  | "plan_id"
  | "amount"
  | "payment_date"
  | "Expire_date"
  | "payment_method"
  | "status"
  | "action";

const ClientPayment: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sortKey, setSortKey] = useState("payment_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [plans, setPlans] = useState<Plan[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: initialSettings.startDate,
    endDate: initialSettings.endDate,
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState<CreatePaymentRequest>({
    client_id: "",
    plan_id: "",
    amount: 0,
    payment_date: "",
    Expire_date: "",
    payment_method: "",
    status: "Active",
  });
  const [editFormData, setEditFormData] = useState<
    UpdatePaymentRequest & {
      payment_id?: string;
      client_name?: string;
      plan_name?: string;
    }
  >({
    client_id: "",
    amount: 0,
    payment_date: "",
    Expire_date: "",
    payment_method: "",
    status: "Active",
    payment_id: "",
    client_name: "",
    plan_name: "",
  });
  const [visibleColumns, setVisibleColumns] = useState<PaymentColumn[]>([
    "payment_id",
    "client_id",
    "plan_id",
    "amount",
    "payment_date",
    "Expire_date",
    "payment_method",
    "status",
    "action",
  ]);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [paymentData, clientData, planData] = await Promise.all([
        getAllPayments(),
        fetchClients(),
        fetchPlans(),
      ]);
      setPayments(paymentData);
      setFilteredPayments(paymentData);
      setClients(clientData);
      setPlans(planData);
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };
  const clientFilteredPayments = payments
    .filter((payment) => {
      const clientName =
        clients
          .find((c) => c.client_id === payment.client_id)
          ?.client_name.toLowerCase() || "";
      const planName =
        plans
          .find((p) => p.plan_id === payment.plan_id)
          ?.plan_name.toLowerCase() || "";
      return (
        clientName.includes(searchQuery) ||
        planName.includes(searchQuery) ||
        payment.payment_id.toLowerCase().includes(searchQuery) ||
        payment.payment_method.toLowerCase().includes(searchQuery)
      );
    })
    .filter((payment) => {
      if (!dateRange.startDate || !dateRange.endDate) return true;
      const date = new Date(payment.payment_date).getTime();
      return (
        date >= dateRange.startDate.getTime() &&
        date <= dateRange.endDate.getTime()
      );
    })
    .sort((a, b) => {
      const aValue = a[sortKey as keyof Payment];
      const bValue = b[sortKey as keyof Payment];

      // ✅ String fields (e.g., payment_id, payment_method)
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // ✅ Valid date strings (e.g., created_at, payment_date)
      if (
        typeof aValue === "string" &&
        typeof bValue === "string" &&
        !isNaN(Date.parse(aValue)) &&
        !isNaN(Date.parse(bValue))
      ) {
        const aDate = new Date(aValue).getTime();
        const bDate = new Date(bValue).getTime();
        return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
      }

      return 0;
    });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
  };

  const handleEditFormChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev: any) => ({
      ...prev,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
  };

  const handleToggleColumn = (column: PaymentColumn) => {
    setVisibleColumns((prev) =>
      prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    try {
      const newPayment = await createPayment(formData);
      setPayments((prev) => [...prev, newPayment]);
      setFilteredPayments((prev) => [...prev, newPayment]);
      setFormData({
        client_id: "",
        plan_id: "",
        amount: 0,
        payment_date: "",
        Expire_date: "",
        payment_method: "",
        status: "Active",
      });
      fetchData();
      toast.success("Payment created successfully!");
      const offcanvasElement = document.getElementById("offcanvas_add");
      if (offcanvasElement) {
        const bsOffcanvas = (
          window as any
        ).bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);
        bsOffcanvas.hide();
      }
    } catch (err) {
      setError("Failed to create payment");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (paymentId: string) => {
    try {
      const payment = await getPaymentById(paymentId);
      const client = clients.find((c) => c.client_id === payment.client_id);
      const plan = plans.find((p) => p.plan_id === payment.plan_id);
      setEditFormData({
        client_id: payment.client_id,
        amount: payment.amount,
        payment_date: payment.payment_date,
        Expire_date: payment.Expire_date,
        payment_method: payment.payment_method,
        status: payment.status,
        payment_id: payment.payment_id,
        client_name: client?.client_name || "Unknown",
        plan_name: plan?.plan_name || "Unknown",
      });

      const offcanvasElement = document.getElementById("offcanvas_edit");
      if (offcanvasElement) {
        const bsOffcanvas = (
          window as any
        ).bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);

        bsOffcanvas.show();
      }
    } catch (err) {
      setError("Failed to load payment details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editFormData.payment_id) return;
    setLoading(true);
    try {
      const updatedPayment = await updatePayment(editFormData.payment_id, {
        client_id: editFormData.client_id,
        amount: editFormData.amount,
        payment_date: editFormData.payment_date,
        Expire_date: editFormData.Expire_date,
        payment_method: editFormData.payment_method,
        status: editFormData.status,
      });
      setPayments((prev) =>
        prev.map((p) =>
          p.payment_id === updatedPayment.payment_id ? updatedPayment : p
        )
      );
      setFilteredPayments((prev) =>
        prev.map((p) =>
          p.payment_id === updatedPayment.payment_id ? updatedPayment : p
        )
      );
      fetchData();
      const offcanvasElement = document.getElementById("offcanvas_edit");
      if (offcanvasElement) {
        const bsOffcanvas = (
          window as any
        ).bootstrap.Offcanvas.getOrCreateInstance(offcanvasElement);
        bsOffcanvas.hide();
      }
    } catch (err) {
      setError("Failed to update payment");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (payment_id: string) => {
    setPaymentToDelete(payment_id);
  };

  const confirmDelete = async () => {
    if (!paymentToDelete) return;
    setLoading(true);
    try {
      await deletePayment(paymentToDelete);
      setPayments((prev) =>
        prev.filter((p) => p.payment_id !== paymentToDelete)
      );
      setFilteredPayments((prev) =>
        prev.filter((p) => p.payment_id !== paymentToDelete)
      );
      setPaymentToDelete(null);
      fetchData();
      toast.success("Payment deleted successfully!");
    } catch (err) {
      setError("Failed to delete payment");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format: "csv" | "pdf") => {
    if (format === "csv") {
      const csvContent = [
        [
          "Payment ID",
          "Client Name",
          "Plan Name",
          "Amount",
          "Payment Date",
          "Expire Date",
          "Payment Method",
          "Status",
        ],
        ...filteredPayments.map((p) => [
          p.payment_id,
          clients.find((c) => c.client_id === p.client_id)?.client_name || "",
          plans.find((plan) => plan.plan_id === p.plan_id)?.plan_name || "",
          p.amount,
          p.payment_date,
          p.Expire_date,
          p.payment_method,
          p.status,
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payments.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } else if (format === "pdf") {
      console.log("PDF export not implemented");
    }
  };

  const columns = [
    {
      title: "Payment ID",
      dataIndex: "payment_id",
      render: (text: string) => highlightMatch(text, searchQuery),
      visible: visibleColumns.includes("payment_id"),
    },
    {
      title: "Client Name",
      render: (data: Payment) =>
        highlightMatch(
          clients.find((c) => c.client_id === data.client_id)?.client_name ||
            "Unknown",
          searchQuery
        ),
      visible: visibleColumns.includes("client_id"),
    },
    {
      title: "Plan Name",
      render: (data: Payment) =>
        highlightMatch(
          plans.find((p) => p.plan_id === data.plan_id)?.plan_name || "Unknown",
          searchQuery
        ),
      visible: visibleColumns.includes("plan_id"),
    },
    {
      title: "Amount",
      render: (data: Payment) => `${data.amount.toFixed(2)}`,
      visible: visibleColumns.includes("amount"),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      visible: visibleColumns.includes("payment_date"),
    },
    {
      title: "Expire Date",
      dataIndex: "Expire_date",
      visible: visibleColumns.includes("Expire_date"),
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      render: (text: string) => highlightMatch(text, searchQuery),
      visible: visibleColumns.includes("payment_method"),
    },
    {
      title: "Status",
      render: (data: Payment) => (
        <span
          className={`badge ${
            data.status === "Active" ? "bg-success" : "bg-danger"
          }`}
        >
          {data.status}
        </span>
      ),
      visible: visibleColumns.includes("status"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Payment, index: number) => (
        <div className="dropdown table-action" key={index}>
          <button
            className="btn btn-sm"
            type="button"
            data-bs-toggle="dropdown"
          >
            <i className="ti ti-dots-vertical" />
          </button>
          <div className="dropdown-menu dropdown-menu-end">
            <Link
              className="dropdown-item"
              to="#"
              onClick={(e) => {
          e.preventDefault();
          handleEditClick(record.payment_id); 
          setShowEdit(true); 
        }}
            >
              <i className="ti ti-edit text-primary me-2" />
              Edit
            </Link>
            <Link
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete_payments"
              onClick={() => handleDeleteClick(record.payment_id)}
            >
              <i className="ti ti-trash text-danger me-2" />
              Delete
            </Link>
          </div>
        </div>
      ),
      visible: visibleColumns.includes("action"),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title"><i className="ti ti-report-money text-primary me-2"/>Payment</h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0">
                        {/* Search Modal */}
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Payments"
                          value={searchQuery}
                          onChange={handleSearch}
                        />
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <div className="dropdown me-2">
                          <a
                            href="#"
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-package-export me-2" />
                            Export
                          </a>
                          <div className="dropdown-menu dropdown-menu-end">
                            <a
                              href="#"
                              className="dropdown-item"
                              onClick={() => handleExport("csv")}
                            >
                              Export as CSV
                            </a>
                            <a
                              href="#"
                              className="dropdown-item"
                              onClick={() => handleExport("pdf")}
                            >
                              Export as PDF
                            </a>
                          </div>
                        </div>
                        <button
          type="button"
          className="btn btn-primary justify-content-sm-start"
          onClick={() => setShowAdd(true)}
        >
          <i className="ti ti-square-rounded-plus me-2" />
          Add Payment
        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {/* 🔽 Sort Dropdown */}
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Sort
                        </Link>
                        <div className="dropdown-menu dropdown-menu-start">
                          <ul>
                            {[
                              {
                                label: "Ascending",
                                key: "payment_id",
                                order: "asc",
                              },
                              {
                                label: "Descending",
                                key: "payment_id",
                                order: "desc",
                              },
                              {
                                label: "Recently Added",
                                key: "created_at",
                                order: "desc",
                              },
                              {
                                label: "Recently Viewed",
                                key: "recently_viewed",
                                order: "desc",
                              },
                            ].map(({ label, key, order }) => {
                              const isActive =
                                sortKey === key && sortOrder === order;
                              return (
                                <li key={label}>
                                  <Link
                                    to="#"
                                    className={`dropdown-item d-flex justify-content-between align-items-center ${
                                      isActive ? "fw-bold text-primary" : ""
                                    }`}
                                    onClick={() => {
                                      setSortKey(key);
                                      setSortOrder(order as "asc" | "desc");
                                    }}
                                  >
                                    <span>
                                      <i className="ti ti-circle-chevron-right me-1" />
                                      {label}
                                    </span>
                                    {isActive && <i className="ti ti-check" />}
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>

                      {/* 🗓️ Date Range Picker */}
                      <div className="icon-form">
                        <span className="form-icon">
                          <i className="ti ti-calendar" />
                        </span>
                        <DateRangePicker
                          initialSettings={initialSettings}
                          onCallback={(start, end) => {
                            setDateRange({
                              startDate: start.toDate(),
                              endDate: end.toDate(),
                            });
                          }}
                        >
                          <input
                            className="form-control bookingrange"
                            type="text"
                          />
                        </DateRangePicker>
                      </div>
                    </div>

                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="btn bg-soft-purple text-purple"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <i className="ti ti-columns-3 me-2" />
                          Manage Columns
                        </Link>
                        <div className="dropdown-menu dropdown-menu-md-end dropdown-md p-3">
                          <h4 className="mb-2 fw-semibold">
                            Want to manage datatables?
                          </h4>
                          <p className="mb-3">
                            Please drag and drop your column to reorder your
                            table and enable see option as you want.
                          </p>
                          <div className="border-top pt-3">
                            {[
                              "payment_id",
                              "client_id",
                              "plan_id",
                              "amount",
                              "payment_date",
                              "Expire_date",
                              "payment_method",
                              "status",
                              "action",
                            ].map((column) => (
                              <div
                                key={column}
                                className="d-flex align-items-center justify-content-between mb-3"
                              >
                                <p className="mb-0 d-flex align-items-center">
                                  <i className="ti ti-grip-vertical me-2" />
                                  {column
                                    .replace("_", " ")
                                    .replace(/^\w/, (c) => c.toUpperCase())}
                                </p>
                                <div className="status-toggle">
                                  <input
                                    type="checkbox"
                                    id={`col-${column}`}
                                    className="check"
                                    checked={visibleColumns.includes(
                                      column as PaymentColumn
                                    )}
                                    onChange={() =>
                                      handleToggleColumn(
                                        column as PaymentColumn
                                      )
                                    }
                                  />
                                  <label
                                    htmlFor={`col-${column}`}
                                    className="checktoggle"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive custom-table">
                    <Datatable
                      dataSource={clientFilteredPayments}
                      columns={columns.filter((col) => col.visible !== false)}
                      className="custom-table"
                    
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"],
                      onChange: (page: number, size: number) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      },
                    }}
                    scroll={{ x: "max-content" }} // Enables horizontal scroll
                    components={{
                      header: {
                        cell: (props: any) => (
                          <th
                            {...props}
                            style={{
                              ...props.style,
                              position: "sticky",
                              top: 0,
                              zIndex: 2, // Better layering over scrollbars/fixed columns
                              background: "#fff",
                              boxShadow: "inset 0 -1px 0 #f50e0e", // ✅ fix this invalid color
                            }}
                          />
                        ),
                      },
                    }}
                    />
                  </div>
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="datatable-length" />
                    </div>
                    <div className="col-md-6">
                      <div className="datatable-paginate" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Add Modal */}

           {showAdd && (
        <>
          {/* Backdrop */}
          <div
            className="offcanvas-backdrop fade show"
            onClick={() => setShowAdd(false)}
            style={{ zIndex: 1040 }}
          />
          <div
            className="offcanvas offcanvas-end offcanvas-large show d-block"
            style={{ visibility: "visible", zIndex: 1050 }}
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="fw-semibold" id="offcanvasAddLabel">
                Add Payment
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                onClick={() => setShowAdd(false)}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
          <div className="offcanvas-body">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                  setShowAdd(false); // Close after submit
                }}
              >
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Client Name <span className="text-danger">*</span>
                          </label>
                          <select
                            name="client_id"
                            value={formData.client_id}
                            onChange={handleChange}
                            required
                            className="form-select"
                          >
                            <option value="">-- Select Client --</option>
                            {clients.map((c) => (
                              <option key={c.client_id} value={c.client_id}>
                                {c.client_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Plan Name <span className="text-danger">*</span>
                          </label>
                          <select
                            name="plan_id"
                            value={formData.plan_id}
                            onChange={handleChange}
                            required
                            className="form-select"
                          >
                            <option value="">-- Select Plan --</option>
                            {plans.map((p) => (
                              <option key={p.plan_id} value={p.plan_id}>
                                {p.plan_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Amount <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            min={0.01}
                            step={0.01}
                            required
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Payment Method{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            name="payment_method"
                            value={formData.payment_method}
                            onChange={handleChange}
                            required
                            className="form-select"
                          >
                            <option value="">
                              -- Select Payment Method --
                            </option>
                            <option value="Credit Card">Credit Card</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Paypal">Paypal</option>
                            <option value="Cash">Cash</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Payment Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            name="payment_date"
                            value={formData.payment_date}
                            onChange={handleChange}
                            required
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Expire Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            name="Expire_date"
                            value={formData.Expire_date}
                            onChange={handleChange}
                            required
                            className="form-control"
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Status</label>
                          <div className="d-flex gap-3">
                            <label className="form-check-label">
                              <input
                                type="radio"
                                name="status"
                                value="Active"
                                checked={formData.status === "Active"}
                                onChange={handleChange}
                                className="form-check-input me-1"
                              />
                              Active
                            </label>
                            <label className="form-check-label">
                              <input
                                type="radio"
                                name="status"
                                value="Inactive"
                                checked={formData.status === "Inactive"}
                                onChange={handleChange}
                                className="form-check-input me-1"
                              />
                              Inactive
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    onClick={() => setShowAdd(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
          {/* Add Modal */}
          {/* Edit Modal */}
          {showEdit && (
        <>
          {/* Backdrop */}
          <div
            className="offcanvas-backdrop fade show"
            onClick={() => setShowEdit(false)}
            style={{ zIndex: 1040 }}
          />

          {/* Panel */}
          <div
            className="offcanvas offcanvas-end offcanvas-large show d-block"
            style={{ visibility: "visible", zIndex: 1050 }}
          >
            <div className="offcanvas-header border-bottom">
              <h5 className="fw-semibold" id="offcanvasEditLabel">
                Edit Payment
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                onClick={() => setShowEdit(false)}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="offcanvas-body">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateSubmit(e);
                  setShowEdit(false); // Close after update
                }}
                >
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Client Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name="client_name"
                            value={editFormData.client_name}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Plan Name</label>
                          <input
                            type="text"
                            className="form-control"
                            name="plan_name"
                            value={editFormData.plan_name}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Amount <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            name="amount"
                            value={editFormData.amount}
                            onChange={handleEditFormChange}
                            min={0.01}
                            step={0.01}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Payment Method{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            name="payment_method"
                            value={editFormData.payment_method}
                            onChange={handleEditFormChange}
                            required
                            className="form-select"
                          >
                            <option value="">-- Select --</option>
                            <option value="Credit Card">Credit Card</option>
                            <option value="Bank Transfer">Bank Transfer</option>
                            <option value="Paypal">Paypal</option>
                            <option value="Cash">Cash</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">Payment ID</label>
                          <input
                            type="text"
                            className="form-control"
                            name="payment_id"
                            value={editFormData.payment_id}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Payment Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            name="payment_date"
                            value={editFormData.payment_date}
                            onChange={handleEditFormChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="col-form-label">
                            Expire Date <span className="text-danger">*</span>
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            name="Expire_date"
                            value={editFormData.Expire_date}
                            onChange={handleEditFormChange}
                            required
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-0">
                          <label className="col-form-label">Status</label>
                          <div className="d-flex flex-wrap gap-3">
                            <label className="form-check-label">
                              <input
                                type="radio"
                                name="status"
                                value="Active"
                                checked={editFormData.status === "Active"}
                                onChange={handleEditFormChange}
                                className="form-check-input me-1"
                              />
                              Active
                            </label>
                            <label className="form-check-label">
                              <input
                                type="radio"
                                name="status"
                                value="Inactive"
                                checked={editFormData.status === "Inactive"}
                                onChange={handleEditFormChange}
                                className="form-check-input me-1"
                              />
                              Inactive
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-end mt-3">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    onClick={() => setShowEdit(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
          {/* Edit Modal */}
          {/* Delete Modal */}
          <div
            className="modal fade"
            id="delete_payments"
            tabIndex={-1}
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body">
                  <div className="text-center">
                    <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                      <i className="ti ti-trash-x fs-36 text-danger" />
                    </div>
                    <h4 className="mb-2">Delete Payment?</h4>
                    <p className="mb-0">
                      Are you sure you want to delete this payment?
                    </p>
                    <div className="d-flex align-items-center justify-content-center mt-4">
                      <button
                        type="button"
                        className="btn btn-light me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        data-bs-dismiss="modal"
                        onClick={confirmDelete}
                      >
                        Yes, Delete it
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delete Modal */}
        </div>
      </div>
    </div>
  );
};

export default ClientPayment;
